import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { BigButton } from '../../components/BigButton';
import PasswordInput from '../../components/Inputs/PasswordInput';
import PhoneInput from '../../components/Inputs/PhoneInput';
import { GenericTextInput } from '../../components/Inputs/styles';
import animations from '../../helpers/animations';
import { getErrorMessage } from '../../helpers/errorMessages';
import inputMasker from '../../helpers/inputMasker';
import authenticationService from '../../services/authentication.service';
import ForeignerContent from './ForeignerContent';
import { handleToggleLanguages } from '../../i18n';
import {
    AuthModalContentProps,
    signUpPageSchema,
    signUpPageSchemaEn,
} from './logic';
import AddressButton from '../../components/AddressButton';
import { useAddressModalStore } from '../../store/address/addressModalStore';
import useCountryCodes from '../../hooks/useCountryCodes';

type Fields = z.infer<typeof signUpPageSchema>;

export default function SignInColumnContent({
    setCurrentColumn,
}: AuthModalContentProps) {
    const { t, i18n } = useTranslation(undefined, {
        keyPrefix: 'create_account',
    });
    const [isForeigner, setIsForeigner] = useState<boolean>(false);
    const address = useAddressModalStore();

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        setError,
    } = useForm<Fields>({
        resolver: zodResolver(
            i18n.language === 'pt' ? signUpPageSchema : signUpPageSchemaEn
        ),
        mode: 'onBlur',
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isBrazilianCodeNumber, setIsBrazilianCodeNumber] = useState(false);
    const countryCodes = useCountryCodes();

    function handleToggleForeigner() {
        handleToggleLanguages(i18n.language === 'pt');
        setIsForeigner(!isForeigner);
    }

    const onSubmit = async (data: Fields) => {
        if (!address.isValid) {
            return toast.error('Endereço inválido');
        }
        setIsLoading(true);

        try {
            const dateParts = data.birthDate.split('/');
            const birthDateParsed = new Date(
                `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
            );

            const countryId = countryCodes.data!.find(
                d => d.iso2 === address.address.countryIso2
            )!.id;

            const result = await authenticationService.sendSignUpRequest({
                name: data.name,
                birthDate: birthDateParsed,
                document: data.cpf,
                email: data.email,
                foreigner: isForeigner,
                password: data.password,
                passwordConfirmation: data.confirm_password,
                phone: `${data.phoneCountry} ${data.phone}`,
                address: {
                    ...(address.address as any),
                    countryId,
                },
            });
            if (result.error) {
                return setError('root', {
                    message: getErrorMessage(result.error),
                });
            } else if (result.error) {
                return toast.error('Ocorreu um erro ao criar sua conta');
            } else {
                toast.success(
                    'Sua conta foi criada com sucesso, agora basta fazer login'
                );
                setCurrentColumn('login');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onError = (_error: unknown) => {};

    const handleReturnCodeNumber = (codeNumber: string) => {
        codeNumber === '+55'
            ? setIsBrazilianCodeNumber(true)
            : setIsBrazilianCodeNumber(false);
    };

    return (
        <motion.form
            onSubmit={handleSubmit(onSubmit, onError)}
            {...animations.fadeInOut(0.05)}
        >
            <div className="title-wrapper">
                <h2 className="title">{t('title_wrapper.title')}</h2>
                <p className="description">{t('title_wrapper.description')}</p>
                <ForeignerContent handleToggle={handleToggleForeigner} />
            </div>
            <div className="text-inputs-wrapper">
                <div className="row">
                    <div className="input-item" style={{ flexGrow: 1 }}>
                        <label htmlFor="name">
                            {t('text_inputs_wrapper.name_label')}
                        </label>
                        <GenericTextInput
                            id="name"
                            type="text"
                            className=""
                            {...register('name')}
                            aria-invalid={Boolean(errors.name)}
                            placeholder={t(
                                'text_inputs_wrapper.name_placeholder'
                            )}
                        />
                        {errors.name && <span>{errors.name.message}</span>}
                    </div>
                    <div className="input-item">
                        <label htmlFor="bornDate">
                            {t('text_inputs_wrapper.bornDate_label')}
                        </label>
                        <GenericTextInput
                            id="bornDate"
                            type="text"
                            className=""
                            {...register('birthDate', {
                                onChange: inputMasker.handleChange('DATE'),
                            })}
                            aria-invalid={Boolean(errors.birthDate)}
                            placeholder={t(
                                'text_inputs_wrapper.bornDate_placeholder'
                            )}
                            maxLength={inputMasker.getMaxLength('DATE')}
                        />
                        {errors.birthDate && (
                            <span>{errors.birthDate.message}</span>
                        )}
                    </div>
                </div>
                <div className="input-item">
                    <label htmlFor="email">
                        {t('text_inputs_wrapper.email_label')}
                    </label>
                    <GenericTextInput
                        id="email"
                        type="text"
                        className=""
                        {...register('email')}
                        aria-invalid={Boolean(errors.email)}
                        placeholder={t('text_inputs_wrapper.email_placeholder')}
                    />
                    {errors.email && <span>{errors.email.message}</span>}
                </div>
                <div className="row phone-row">
                    <div className="input-item">
                        <label htmlFor="cpf">
                            {t('text_inputs_wrapper.cpf_label')}
                        </label>
                        <GenericTextInput
                            id="cpf"
                            type="text"
                            className=""
                            {...register('cpf', {
                                onChange: !isForeigner
                                    ? inputMasker.handleChange('CPFCNPJ')
                                    : undefined,
                            })}
                            aria-invalid={Boolean(errors.cpf)}
                            placeholder={t(
                                'text_inputs_wrapper.cpf_placeholder'
                            )}
                            maxLength={
                                !isForeigner
                                    ? inputMasker.getMaxLength('CPFCNPJ')
                                    : 9
                            }
                        />
                        {errors.cpf && <span>{errors.cpf.message}</span>}
                    </div>
                    <div className="input-item">
                        <label htmlFor="phone">
                            {t('text_inputs_wrapper.phone_label')}
                        </label>
                        <PhoneInput
                            parentProps={{
                                'aria-invalid': Boolean(
                                    errors.phone || errors.phoneCountry
                                ),
                            }}
                            phoneInputProps={{
                                placeholder: t(
                                    'text_inputs_wrapper.phone_placeholder'
                                ),
                                ...register('phone', {
                                    onChange: isBrazilianCodeNumber
                                        ? inputMasker.handleChange('PHONE')
                                        : () => {},
                                }),
                                id: 'phone',
                            }}
                            countryInputProps={{
                                ...register('phoneCountry', {}),
                            }}
                            handleReturnCodeNumber={handleReturnCodeNumber}
                        />
                        {errors.phone && <span>{errors.phone.message}</span>}
                        {!errors.phone && errors.phoneCountry && (
                            <span>{errors.phoneCountry.message}</span>
                        )}
                    </div>
                </div>
                <div className="input-item" onClick={e => e.stopPropagation()}>
                    <label htmlFor="address">
                        {t('text_inputs_wrapper.address_label')}
                    </label>
                    <AddressButton />
                    {/* {errors.address && <span>{errors.address.message}</span>} */}
                </div>
                <div className="input-item">
                    <label htmlFor="password">
                        {t('text_inputs_wrapper.password_label')}
                    </label>
                    <PasswordInput
                        aria-invalid={Boolean(errors.password)}
                        parentProps={{
                            'aria-invalid': Boolean(errors.password),
                        }}
                        inputProps={{
                            ...register('password'),
                            placeholder: t(
                                'text_inputs_wrapper.password_placeholder'
                            ),
                        }}
                    />
                    {errors.password && <span>{errors.password.message}</span>}
                </div>
                <div className="input-item">
                    <label htmlFor="confirm_password">
                        {t('text_inputs_wrapper.confirm_password_label')}
                    </label>
                    <PasswordInput
                        parentProps={{
                            'aria-invalid': Boolean(errors.confirm_password),
                        }}
                        inputProps={{
                            ...register('confirm_password'),
                            placeholder: t(
                                'text_inputs_wrapper.confirm_password_placeholder'
                            ),
                        }}
                    />
                    {errors.confirm_password && (
                        <span>{errors.confirm_password.message}</span>
                    )}
                </div>
            </div>
            <div className="error-column">
                {errors.root && (
                    <p className="api-error">{errors.root.message!}</p>
                )}
                <BigButton
                    bgColor={isValid ? 'primary' : 'secondary'}
                    color={isValid ? 'primary' : 'secondary'}
                    type="submit"
                    cursorBlocked={!isValid}
                    isLoading={isLoading}
                >
                    {t('big_button.submit_text')}
                </BigButton>
            </div>

            <p className="create-account-notice">
                {t('create_account_notice.text')}{' '}
                <a onClick={() => setCurrentColumn('login')}>
                    {t('create_account_notice.link_text')}
                </a>
            </p>
        </motion.form>
    );
}
